import { computed, inject, Injectable, signal } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, finalize, map, race, switchMap, take, throwError } from 'rxjs';

import { AttachmentsActions, AttachmentsSelectors } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import { generateUuid } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class EvaluationAttachmentsService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private readonly activeOperations = signal(0);

  public readonly isUploading = computed(() => this.activeOperations() > 0);

  public upload(file: File): Observable<Attachment> {
    const correlationId = generateUuid();
    this.activeOperations.update(count => count + 1);

    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({ payload: { file, correlationId } }),
    );

    const success$ = this.store
      .select(AttachmentsSelectors.byId(correlationId))
      .pipe(filterBoolean, take(1));

    const failure$ = this.actions$.pipe(
      ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
      filter(({ payload }) => payload.correlationId === correlationId),
      take(1),
      switchMap(() => throwError(() => new Error('Attachment upload failed'))),
    );

    return race(success$, failure$).pipe(
      take(1),
      finalize(() => this.activeOperations.update(count => Math.max(0, count - 1))),
    );
  }

  public delete(id: number): Observable<Attachment> {
    this.activeOperations.update(count => count + 1);
    this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: id }));

    return this.actions$.pipe(
      ofType(AttachmentsActions.deleteAttachmentSuccess),
      filter(({ payload }) => payload.data.id === id),
      take(1),
      map(({ payload }) => payload.data),
      finalize(() => this.activeOperations.update(count => Math.max(0, count - 1))),
    );
  }
}
